'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/styles/cn'
import { CASE_STUDY_CARDS } from '@/features/landing-page/v3/sections/case-studies-data'

/**
 * lp-v3 case-study carousel. All cards ship in the static HTML (crawlable) inside a CSS scroll-snap
 * track — swipe/scroll works with no JS. The only JS is progressive enhancement: mobile dots that
 * track the scroll position and let you jump between cards. No carousel library.
 */
export function LpCaseStudiesCarousel() {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller) return

        const cards = Array.from(scroller.querySelectorAll<HTMLElement>('[data-cs-card]'))
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveIndex(Number((entry.target as HTMLElement).dataset.csIndex))
                    }
                }
            },
            { root: scroller, threshold: 0.6 }
        )
        cards.forEach((card) => observer.observe(card))

        return () => observer.disconnect()
    }, [])

    const goTo = (index: number) => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>(`[data-cs-index="${index}"]`)
        card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }

    return (
        <div className="relative">
            {/* Scroll gradients: soft cream fades on both edges hint that the row scrolls. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 md:w-24"
                style={{ background: 'linear-gradient(to right, hsl(var(--background)), transparent)' }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 md:w-24"
                style={{ background: 'linear-gradient(to left, hsl(var(--background)), transparent)' }}
            />
            <div
                ref={scrollerRef}
                className="flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto scroll-smooth pb-2 pl-6 scroll-pl-6 md:gap-5 md:pl-10 md:scroll-pl-10 lg:pl-[60px] lg:scroll-pl-[60px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                {CASE_STUDY_CARDS.map((card, index) => (
                    <article
                        key={card.id}
                        data-cs-card
                        data-cs-index={index}
                        className="flex w-[82vw] max-w-[360px] shrink-0 snap-start flex-col rounded-[16px] bg-[#F4F2EE] p-7 md:w-[560px] md:max-w-none md:p-9"
                    >
                        <div className="flex items-center gap-2.5">
                            <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
                                <Image
                                    src={card.logoUrl}
                                    alt={`${card.company} logo`}
                                    width={32}
                                    height={32}
                                    className="size-full object-cover"
                                />
                            </span>
                            <span className="text-[15px]! font-medium text-foreground">{card.company}</span>
                        </div>

                        {/* KPI = Replica XL (100/110) ink; stat label = S (24/110) ink */}
                        <div className="mt-8 md:mt-10">
                            <div className="text-[56px]! font-normal leading-[1.1]! text-foreground md:text-[100px]!">
                                {card.statValue}
                            </div>
                            <div className="mt-2 text-[24px]! font-normal leading-[1.1]! text-foreground">
                                {card.statLabel}
                            </div>
                        </div>

                        {/* Description = S (24/110) stone */}
                        <p className="mt-auto pt-10 text-[24px]! font-normal leading-[1.15]! text-[#746C64]!">
                            {card.summary}
                        </p>

                        {card.href && (
                            <a
                                href={card.href}
                                className="mt-5 inline-flex w-fit text-[16px]! font-normal leading-[1.1]! text-[#746C64]! underline decoration-1 underline-offset-[3px] transition-colors hover:text-[#131210]!"
                            >
                                Read case study
                            </a>
                        )}
                    </article>
                ))}
                {/* Trailing spacer so the last card keeps its right-edge padding inside the scroll track. */}
                <div aria-hidden className="shrink-0 pl-6 md:pl-10 lg:pl-[60px]" />
            </div>

            {/* Mobile pagination dots — enhancement only; the track scrolls natively without them. */}
            <div className="mt-6 flex justify-center gap-2 md:hidden">
                {CASE_STUDY_CARDS.map((card, index) => (
                    <button
                        key={card.id}
                        type="button"
                        aria-label={`Go to ${card.company}`}
                        onClick={() => goTo(index)}
                        className={cn(
                            'size-1.5 rounded-full transition-colors',
                            index === activeIndex ? 'bg-[#FF5500]' : 'bg-foreground/20'
                        )}
                    />
                ))}
            </div>
        </div>
    )
}
